/**
 * @file src/_modulos/prenota/tabela/layouts/config/content/filters.ts
 * @description Filtros simplificados para a tabela de pré-notas
 * 
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas gerencia filtros
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useCatalogs, useUser } from "store";
import { useTableStore, PRENOTAS_QUERY_KEY } from "@PN/tabela";
import {
  FILTER_COLUMNS,
  DEFAULT_OPERATOR,
  TIPOS_NF_OPTIONS,
  STATUS_NF_OPTIONS,
  QUICK_FILTER_FIELDS,
  FILTER_FLAGS,
  TAB_CONFIGS,
} from "./constants";
import { useQueryClient, useQuery } from "@tanstack/react-query";

/* -------------------------------------------------------------------------- */
/* UTILITÁRIOS                                                                */
/* -------------------------------------------------------------------------- */

export const formatDate = (date: Date): string => {
  return format(date, "dd/MM/yyyy", { locale: ptBR });
};

export const parseDate = (dateString: string): Date | undefined => {
  if (!dateString) return undefined;
  const [day, month, year] = dateString.split("/");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

export const getColumnType = (columnKey: string): string => {
  const column = FILTER_COLUMNS.find((col) => col.key === columnKey);
  return column?.type || "text";
};

/* -------------------------------------------------------------------------- */
/* HOOKS SIMPLIFICADOS                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Hook para carregar condições de pagamento da API
 */
export function useCondicoesOptions() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["condicoes-pagamento"],
    queryFn: async () => {
      const response = await fetch("/api/prenotas/condicoes");
      if (!response.ok) {
        throw new Error("Erro ao carregar condições de pagamento");
      }
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });

  return {
    condicoesOptions: data?.data || [],
    isLoading,
    error,
  };
}

/**
 * Hook para filtros rápidos
 */
export function useQuickFilters() {
  const { setAppliedFilters, appliedFilters } = useTableStore();
  const { filiais } = useCatalogs();
  const queryClient = useQueryClient();
  const lastInvalidation = useRef<number>(0);

  // Invalida query quando filtros mudam (com debounce)
  useEffect(() => {
    const hasQuickFilters = Object.keys(appliedFilters).some(
      (key) =>
        key === QUICK_FILTER_FIELDS.FILIAL ||
        key === QUICK_FILTER_FIELDS.STATUS ||
        key === QUICK_FILTER_FIELDS.TIPO
    );

    if (hasQuickFilters) {
      const now = Date.now();
      if (now - lastInvalidation.current > 500) { // Debounce de 500ms
        lastInvalidation.current = now;
        queryClient.invalidateQueries({ queryKey: PRENOTAS_QUERY_KEY });
      }
    }
  }, [appliedFilters, queryClient]);

  // Opções de filtro
  const filterOptions = useMemo(
    () => ({
      filialOptions: filiais.options?.map((option) => ({
        value: option.value,
        label: option.label,
      })) || [],
      statusOptions: STATUS_NF_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
      })),
      tipoOptions: TIPOS_NF_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    }),
    [filiais.options]
  );

  // Valores atuais
  const currentFilters = useMemo(
    () => ({
      currentFilial: (appliedFilters[QUICK_FILTER_FIELDS.FILIAL] as string[]) || [],
      currentStatus: (appliedFilters[QUICK_FILTER_FIELDS.STATUS] as string[]) || [],
      currentTipo: (appliedFilters[QUICK_FILTER_FIELDS.TIPO] as string[]) || [],
    }),
    [appliedFilters]
  );

  // Handlers
  const handlers = useMemo(
    () => ({
      handleFilialChange: (value: string[] | undefined) => {
        const newFilters = { ...appliedFilters };
        if (value && value.length > 0) {
          newFilters[QUICK_FILTER_FIELDS.FILIAL] = value;
        } else {
          delete newFilters[QUICK_FILTER_FIELDS.FILIAL];
        }
        setAppliedFilters(newFilters);
      },
      handleStatusChange: (value: string[] | undefined) => {
        const newFilters = { ...appliedFilters };
        if (value && value.length > 0) {
          newFilters[QUICK_FILTER_FIELDS.STATUS] = value;
        } else {
          delete newFilters[QUICK_FILTER_FIELDS.STATUS];
        }
        setAppliedFilters(newFilters);
      },
      handleTipoChange: (value: string[] | undefined) => {
        const newFilters = { ...appliedFilters };
        if (value && value.length > 0) {
          newFilters[QUICK_FILTER_FIELDS.TIPO] = value;
        } else {
          delete newFilters[QUICK_FILTER_FIELDS.TIPO];
        }
        setAppliedFilters(newFilters);
      },
    }),
    [appliedFilters, setAppliedFilters]
  );

  // Formatters
  const formatters = useMemo(
    () => ({
      getFilialTooltipContent: (filials: string[]) => {
        if (filials.length === 0) return "Todas as filiais";
        if (filials.length === 1) return `Filia: ${filials[0]}`;
        return `${filials.length} filiais selecionadas`;
      },
      getStatusTooltipContent: (statuses: string[]) => {
        if (statuses.length === 0) return "Todos os status";
        if (statuses.length === 1) return `Status: ${statuses[0]}`;
        return `${statuses.length} status selecionados`;
      },
      getTipoTooltipContent: (tipos: string[]) => {
        if (tipos.length === 0) return "Todos os tipos";
        if (tipos.length === 1) return `Tipo: ${tipos[0]}`;
        return `${tipos.length} tipos selecionados`;
      },
    }),
    []
  );

  return {
    filterOptions,
    currentFilters,
    handlers,
    formatters,
  };
}

/**
 * Hook para filtros avançados
 */
export function useAdvancedFilters() {
  const { appliedFilters, setAppliedFilters, removeFilter, sorting, setSorting } = useTableStore();
  const queryClient = useQueryClient();
  const lastInvalidation = useRef<number>(0);
  const { condicoesOptions } = useCondicoesOptions();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [currentFilter, setCurrentFilter] = useState<any>({});

  // Invalida query quando filtros mudam (com debounce)
  useEffect(() => {
    if (Object.keys(appliedFilters).length > 0) {
      const now = Date.now();
      if (now - lastInvalidation.current > 500) { // Debounce de 500ms
        lastInvalidation.current = now;
        queryClient.invalidateQueries({ queryKey: PRENOTAS_QUERY_KEY });
      }
    }
  }, [appliedFilters, queryClient]);

  // Filtros ativos
  const activeFilters = Object.entries(appliedFilters)
    .filter(([key]) => key !== FILTER_FLAGS.SEM_BOLETO)
    .map(([key, value]) => {
      const column = FILTER_COLUMNS.find((col) => col.key === key);
      const displayValue = Array.isArray(value) ? value.join(", ") : String(value);
      return {
        id: key,
        column: key,
        operator: "is",
        value,
        label: `${column?.label || key} = ${displayValue}`,
      };
    });

  // Handlers
  const handlers = useMemo(
    () => ({
      handleColumnSelect: (columnKey: string) => {
        const column = FILTER_COLUMNS.find((col) => col.key === columnKey);
        if (column) {
          setSelectedColumn(columnKey);
          setCurrentFilter({
            id: `${columnKey}_${Date.now()}`,
            column: columnKey,
            operator: DEFAULT_OPERATOR,
            value: "",
            label: "",
          });
        }
      },
      handleApplyFilter: () => {
        if (!currentFilter.column) return;
        const key = String(currentFilter.column);
      
        // range de valores numéricos
        if (key === "F1_VALBRUT") {
          const hasMin = currentFilter.minValue !== "" && currentFilter.minValue != null;
          const hasMax = currentFilter.maxValue !== "" && currentFilter.maxValue != null;
          
          if (!hasMin && !hasMax) return;
      
          const newFilters = {
            ...appliedFilters,
            [key]: {
              min: hasMin ? Number(currentFilter.minValue) : undefined,
              max: hasMax ? Number(currentFilter.maxValue) : undefined,
            },
          };

          setAppliedFilters(newFilters);
          setCurrentFilter({});
          setIsDropdownOpen(false);
          return;
        }
        // filtros simples (text, select, date)
        if (currentFilter.value) {
          const raw = String(currentFilter.value);
          const newFilters = {
            ...appliedFilters,
            [key]:
              key === "A2_NOME" && /^[\d.\-\/]+$/.test(raw) // só números + .-/ ?
                ? raw.replace(/\D/g, "") // deixa só dígitos
                : raw,
          };
        
          setAppliedFilters(newFilters);
          setCurrentFilter({});
          setIsDropdownOpen(false);
        }        
      },
      
      handleRemoveFilter: (filterId: string) => {
        removeFilter(filterId);
      },
      handleClearAllFilters: () => {
        setAppliedFilters({});
      },
      handleClearClassification: () => {
        setSorting([]);
      },
      setIsDropdownOpen,
      setCurrentFilter,
    }),
    [currentFilter, appliedFilters, setAppliedFilters, removeFilter, setSorting]
  );

  // Helpers
  const helpers = useMemo(
    () => ({
      getColumnType,
      getCurrentValue: (columnKey: string) => {
        return currentFilter.column === columnKey ? currentFilter.value : "";
      },
      formatDate,
      parseDate,
    }),
    [currentFilter]
  );

  return {
    dropdownState: { isOpen: isDropdownOpen, selectedColumn, currentFilter },
    activeFilters: activeFilters.map((f) => {
      if (f.column === "F1_VALBRUT" && typeof f.value === "object" && f.value) {
        const min = (f.value as any).minValue;
        const max = (f.value as any).maxValue;
        let labelSuffix = "";
        if (min != null && max != null) {
          labelSuffix = `${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(min)} - ${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(max)}`;
        } else if (min != null) {
          labelSuffix = `≥ ${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(min)}`;
        } else if (max != null) {
          labelSuffix = `≤ ${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(max)}`;
        }
        return {
          ...f,
          label: `${(FILTER_COLUMNS.find((c) => c.key === f.column)?.label || f.column)} = ${labelSuffix}`,
        };
      }
      return f;
    }),
    appliedFilters,
    sorting,
    handlers,
    helpers,
    condicoesOptions,
  };
}

/**
 * Hook para inicializar filiais
 */
export function useInitFilials(
  accessibleFilialCodes: string[],
  currentFilials: string[],
  setFilials: (filials: string[]) => void
): void {
  useEffect(() => {
    if (currentFilials.length === 0 && accessibleFilialCodes.length > 0) {
      setFilials(accessibleFilialCodes);
    }
  }, [accessibleFilialCodes, currentFilials, setFilials]);
}

/**
 * Hook para filtros por tab
 */
export function useTabFilters() {
  const { setAppliedFilters, clearAppliedFilters } = useTableStore();
  const user = useUser();

  const applyTabFilter = useCallback(
    (tabKey: string) => {
      const tabConfig = TAB_CONFIGS.find((t) => t.key === tabKey);
      if (!tabConfig) return;

      const filter = { ...tabConfig.filter } as any;
      if (filter.F1_XUSRRA === "current" && user.login) {
        filter.F1_XUSRRA = user.login;
      }
      
      setAppliedFilters(filter);
    },
    [setAppliedFilters, user.login]
  );

  const clearTabFilters = useCallback(() => {
    clearAppliedFilters();
  }, [clearAppliedFilters]);

  return {
    applyTabFilter,
    clearTabFilters,
  };
}
